import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { SanctionsProvider } from "./context/SanctionsContext";
import AppErrorBoundary from "./components/AppErrorBoundary";
// Порядок важен: токены объявляют переменные, global.css их использует
// и может переопределить; base.css добавляет фокус/скроллбар поверх обоих.
import "./styles/tokens.css";
import "./styles/global.css";
import "./styles/base.css";
import App from "./App";

// DevLog — общий лог-стрим всех фронтов в один файл (devlog.txt).
// Тег [rs] отличает сообщения от Майнкрафтии ([mnk]) и плеера ([player]).
// На localhost подключается, на проде сервер недоступен и хук тихо
// отключается после первой неудачной попытки.
if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
  import('./devlog/devlog').then((m) => {
    m.attachConsoleHook('rs');
    console.log('rublox-site boot');
  });
}

// SSO с Майнкрафтии: если в URL есть #ticket=... или ?ticket=... —
// меняем тикет на JWT через /api-user/api/v1/auth/redeem-ticket и сохраняем
// его в localStorage до того как стартует AuthProvider. Тогда юзер сразу
// залогинен. Hash/query очищаем чтобы не оставлять ticket в URL.
async function redeemSsoTicketIfPresent() {
  if (typeof window === 'undefined') return;
  try {
    let ticket = null;
    // hash: #ticket=xxx или #ticket=xxx&other=...
    const hash = (window.location.hash || '').replace(/^#/, '');
    if (hash) {
      const params = new URLSearchParams(hash);
      ticket = params.get('ticket');
    }
    // query: ?ticket=xxx
    if (!ticket) {
      const q = new URLSearchParams(window.location.search);
      ticket = q.get('ticket');
    }
    if (!ticket) return;
    // Если в localStorage уже есть JWT — не дёргаем redeem (юзер залогинен).
    // Тикет одноразовый — сжигать его без нужды нельзя.
    const existing = localStorage.getItem('rublox_token');
    if (existing) {
      // Чистим URL и выходим
      const url = new URL(window.location.href);
      url.hash = ''; url.searchParams.delete('ticket');
      window.history.replaceState({}, '', url.toString());
      return;
    }
    // Тикет мог выпустить один из двух сервисов: новый identity v2 (школа
    // переезжает на него) или старый user-сервис v1. Снаружи они неразличимы —
    // оба выдают 32 hex-символа. Поэтому пробуем по очереди.
    //
    // ВАЖЕН ПОРЯДОК: тикет одноразовый, но «чужой» сервис его не знает и
    // вернёт 401, ничего не сжигая. Сначала спрашиваем v2 как основной
    // источник, при отказе — v1.
    const API_BASE = window.location.hostname === 'localhost'
      ? 'https://minecraftia-school.ru'
      : '';
    // identity v2 живёт ТОЛЬКО на домене школы: на rublox.pro путь
    // /api/v2/... не проксируется и попадает в SPA-fallback (отдаётся HTML,
    // а не JSON) — проверено. Поэтому здесь всегда абсолютный адрес школы,
    // а не относительный путь, как у v1.
    const SCHOOL = 'https://minecraftia-school.ru';
    const ENDPOINTS = [
      `${SCHOOL}/api/v2/identity/sso/redeem-ticket`,     // новый источник правды
      `${API_BASE}/api-user/api/v1/auth/redeem-ticket`,  // пока Рублокс не переведён
    ];
    for (const url of ENDPOINTS) {
      let resp;
      try {
        resp = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ticket }),
        });
      } catch {
        continue;                       // сеть/недоступен — пробуем следующий
      }
      if (!resp.ok) continue;           // 401 у чужого сервиса — это нормально
      const data = await resp.json().catch(() => null);
      // v2 отдаёт токен и как jwt, и как token (плеер/студия читают jwt,
      // сайт — token); v1 отдаёт token + refreshToken.
      const tok = data?.token || data?.jwt;
      if (!tok) continue;
      localStorage.setItem('rublox_token', tok);
      if (data?.refreshToken) localStorage.setItem('rublox_refresh', data.refreshToken);
      break;
    }
  } catch (e) {
    // Тикет протух / сеть оборвалась — молча игнорируем, юзер увидит логин-форму.
  } finally {
    // Всегда чистим URL, чтобы тикет не висел в адресной строке.
    try {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.delete('ticket');
      window.history.replaceState({}, '', url.toString());
    } catch {}
  }
}

const root = ReactDOM.createRoot(document.getElementById("root"));

// Сначала redeem SSO-тикета (если есть), потом рендерим React.
// Это гарантирует что AuthProvider при инициализации увидит уже сохранённый JWT.
redeemSsoTicketIfPresent().finally(() => {
  root.render(
    <React.StrictMode>
      {/* Граница ВЫШЕ провайдеров и роутера — ловит в том числе их
          собственные падения, а не только ошибки страниц. */}
      <AppErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <SanctionsProvider>
              <App />
            </SanctionsProvider>
          </AuthProvider>
        </BrowserRouter>
      </AppErrorBoundary>
    </React.StrictMode>
  );
});
